import {
    HttpClient,
    HttpErrorResponse,
    HttpParams,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivate,
    CanActivateChild,
    NavigationEnd,
    Router,
    RouterStateSnapshot,
} from '@angular/router';
import {
    BehaviorSubject,
    Observable,
    of,
    Subscription,
    throwError,
    timer,
} from 'rxjs';
import {
    catchError,
    concatMap,
    filter,
    map,
    switchMap,
    take,
    tap,
} from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../app.config';
import { CookieService } from '../common/cookie.service';
import { mapList, mapRetrieve, mapUpdate } from '../common/rest.operators';
import { I18nLoaderService } from '../i18n/loader.i18n.service';
import {
    ExtSecurityQuestion,
    SecurityQuestion,
    SecurityQuestionModelTransformer,
} from '../user/user.model';

import {
    AuthState,
    AuthStatus,
    AuthUser,
    EffectiveUserPermission,
    EffectiveUserPermissionModelSetTransformer,
    ExtEffectiveUserPermission,
    ExtResetPasswordToken,
    ExtToken,
    ExtVerifyResetPasswordToken,
    LoginModelTransformer,
    Password,
    PasswordModelTransformer,
    ResetPassword,
    ResetPasswordModelTransformer,
    ResetPasswordSecQuesAns,
    ResetPasswordSecQuesAnsModelTransformer,
    Token,
    TokenRequest,
} from './auth.model';

@Injectable()
export class AuthService implements CanActivate, CanActivateChild {
    private readonly _storageKeyPrefix: string;
    authState: AuthState;
    authStatus: BehaviorSubject<AuthStatus>;
    private _idleCheckSub: Subscription;
    private _authCheckSub: Subscription;
    private _lastNavigation: number;
    user: AuthUser;

    constructor(
        private readonly _router: Router,
        private readonly _http: HttpClient,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
        private readonly _i18nLoaderService: I18nLoaderService,
        private readonly _cookieService: CookieService,
    ) {
        this._storageKeyPrefix = this._appConfig.OAUTH.AUTHORIZATION_ENDPOINT;

        this.authState = AuthState.UNSET;

        this.authStatus = new BehaviorSubject<AuthStatus>(
            AuthStatus.UNAUTHENTICATED,
        );

        this._router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            // eslint-disable-next-line no-return-assign
            .subscribe(() => (this._lastNavigation = Date.now()));
    }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): Observable<boolean> | Promise<boolean> | boolean {
        const unAuthOnlyPaths = ['login'];
        // eslint-disable-next-line @typescript-eslint/prefer-includes
        const isUnAuthOnlyPath =
            // eslint-disable-next-line @typescript-eslint/prefer-includes
            unAuthOnlyPaths.indexOf(route.routeConfig.path) !== -1;
        const isAuthenticated =
            this.authStatus.value === AuthStatus.AUTHENTICATED;
        if (isAuthenticated && isUnAuthOnlyPath) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this._router.navigate([this._appConfig.DEFAULT_ROUTER_PATH]);
            return false;
        }
        if (!isAuthenticated && !isUnAuthOnlyPath) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this._router.navigate(['login'], {
                queryParams: { returnTo: state.url },
            });
            return false;
        }
        const result =
            (isAuthenticated && !isUnAuthOnlyPath) ||
            (!isAuthenticated && isUnAuthOnlyPath);
        // TODO revise. lazy loaded modules
        if (result && this._i18nLoaderService.pending) {
            return this._i18nLoaderService.events.pipe(
                filter((event) => event.type === 'load'),
                take(1),
                map(() => true),
            );
        }
        return result;
    }

    canActivateChild(
        childRoute: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): Observable<boolean> | Promise<boolean> | boolean {
        return this.canActivate(childRoute, state);
    }

    checkToken(): Observable<AuthUser> {
        return of(null).pipe(
            switchMap(() => {
                this.user = this._setup(this.user);
                if (this.user.isAuthenticated()) {
                    this.updateAuthStatus(AuthState.LOCAL_STORAGE);
                    return of(this.user);
                } else {
                    return this._refreshToken({
                        refreshToken: this.user.token.refresh_token,
                    });
                }
            }),
            catchError((err) => {
                console.log(err);
                this.updateAuthStatus();
                return of(null);
            }),
        );
    }

    private _setup(user: AuthUser): AuthUser {
        const token = Token.fromJson(
            localStorage.getItem(`${this._storageKeyPrefix}_token`),
        );

        if (!user) {
            user = new AuthUser();
        }
        user.setAuth(token);
        return user;
    }

    private _cleanup(): void {
        if (this.user) {
            this.user.setAuth(null);
            this.user.permissions = null;
        }

        localStorage.removeItem(`${this._storageKeyPrefix}_token`);
    }

    public updateAuthStatus(authState: AuthState = AuthState.UNSET): void {
        console.log(`auth: authState=${AuthState[authState]}`);

        if (this._idleCheckSub) {
            this._idleCheckSub.unsubscribe();
        }
        if (this._authCheckSub) {
            this._authCheckSub.unsubscribe();
        }

        const authed =
            [
                AuthState.UNSET,
                AuthState.LOCAL_STORAGE,
                AuthState.INTERACTIVE,
                AuthState.NON_INTERACTIVE,
            ].includes(authState) &&
            this.user &&
            this.user.isAuthenticated();
        if (authed) {
            const minIdleTime = (this.user.token.expires_in / 2) * 1000;
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            const didTimeout = () => false;

            this._idleCheckSub = timer(minIdleTime, minIdleTime).subscribe(
                () => {
                    if (didTimeout()) {
                        this.updateAuthStatus(AuthState.TIMEOUT);
                    }
                },
                null,
            );

            this._authCheckSub = timer(
                Math.abs(this.user.token.expires_at.getTime() - Date.now()) -
                    10000,
            )
                .pipe(
                    concatMap(() => {
                        if (didTimeout()) {
                            return throwError('timeout');
                        }
                        return this._refreshToken({
                            refreshToken: this.user.token.refresh_token,
                        });
                    }),
                )
                .subscribe(null, (error) => {
                    this.updateAuthStatus(
                        error !== 'timeout'
                            ? AuthState.LOST
                            : AuthState.TIMEOUT,
                    );
                });
        } else {
            this._cleanup();
        }

        this.authState = authState;
        this.authStatus.next(
            authed
                ? AuthStatus.AUTHENTICATED
                : authState === AuthState.TIMEOUT ||
                  authState === AuthState.LOST
                ? AuthStatus.AUTHENTICATION_LOST
                : AuthStatus.UNAUTHENTICATED,
        );
    }

    private _login(
        obs: Observable<ExtToken>,
        authState: AuthState = AuthState.INTERACTIVE,
    ): Observable<AuthUser> {
        return obs.pipe(
            map((model) => new LoginModelTransformer().transform(model)),
            tap((token: Token) =>
                localStorage.setItem(
                    `${this._storageKeyPrefix}_token`,
                    token.toJSON(),
                ),
            ),
            map(() => this._setup(this.user)),
            tap((user) => {
                this.user = user;
                this.updateAuthStatus(authState);
            }),
            catchError((error) => {
                console.error(error);
                if (error instanceof HttpErrorResponse) {
                    throw new Error(error.error.error_description);
                }
                throw new Error();
            }),
        );
    }

    determineAuthenticationMethods(username: string): Observable<string[]> {
        const params = new HttpParams().set('username', username);
        return this._http
            .get(
                `${this._appConfig.OAUTH.AUTHORIZATION_ENDPOINT}/authentication-methods`,
                { params },
            )
            .pipe(
                mapList(),
                catchError(() => {
                    return of([]);
                }),
            );
    }

    login(model: TokenRequest): Observable<AuthUser> {
        const body = new LoginModelTransformer(
            this._appConfig.OAUTH.CLIENT_ID,
            'password',
            this._appConfig.OAUTH.SCOPES,
        ).reverseTransform(model);

        return this._login(
            this._http.post<ExtToken>(
                `${this._appConfig.OAUTH.AUTHORIZATION_ENDPOINT}/connect/token`,
                body,
            ),
        );
    }

    loginByAuthorizationCode(model: TokenRequest): Observable<AuthUser> {
        const body = new LoginModelTransformer(
            this._appConfig.OAUTH.CLIENT_ID,
            'authorization_code',
            this._appConfig.OAUTH.SCOPES,
        ).reverseTransform(model);

        return this._login(
            this._http.post<ExtToken>(
                `${this._appConfig.OAUTH.AUTHORIZATION_ENDPOINT}/connect/token`,
                body,
            ),
        );
    }

    loginByTokenFragment(tokenFragment: string): Observable<AuthUser> {
        return this._login(
            of(tokenFragment).pipe(
                map((tokenFragment) => {
                    return tokenFragment.split('&').reduce((a, b) => {
                        const index = b.indexOf('=');
                        if (index !== -1) {
                            a[b.substring(0, index)] = b.substring(index + 1);
                        }
                        return a;
                    }, {}) as ExtToken;
                }),
            ),
        );
    }

    refreshToken(): Observable<AuthUser> {
        return this._refreshToken({
            refreshToken: this.user.token.refresh_token,
        });
    }

    private _refreshToken(model: TokenRequest): Observable<AuthUser> {
        const body = new LoginModelTransformer(
            this._appConfig.OAUTH.CLIENT_ID,
            'refresh_token',
        ).reverseTransform(model);

        return this._login(
            this._http.post<ExtToken>(
                `${this._appConfig.OAUTH.AUTHORIZATION_ENDPOINT}/connect/token`,
                body,
            ),
            AuthState.NON_INTERACTIVE,
        );
    }

    logout(): Observable<void> {
        this.updateAuthStatus(AuthState.LOGOUT);
        return of(null);
    }

    validateCredentials(model: TokenRequest): Observable<ExtToken> {
        const body = new LoginModelTransformer(
            this._appConfig.OAUTH.CLIENT_ID,
            'password',
            this._appConfig.OAUTH.SCOPES,
        ).reverseTransform(model);

        return this._http
            .post<ExtToken>(
                `${this._appConfig.OAUTH.AUTHORIZATION_ENDPOINT}/connect/token`,
                body,
            )
            .pipe(
                catchError((error) => {
                    console.error(error);
                    if (error instanceof HttpErrorResponse) {
                        throw new Error(error.error.error_description);
                    }
                    throw new Error();
                }),
            );
    }

    requestResetPasswordTokenByEmail(
        email: string,
        languageId: number,
    ): Observable<boolean> {
        const params = new HttpParams()
            .set('email', email)
            .set('languageId', languageId.toString());

        return this._http
            .get(
                `${this._appConfig.OAUTH.AUTHORIZATION_ENDPOINT}/account/PasswordRecoveryChallengeByEmail`,
                { params },
            )
            .pipe(
                mapUpdate(),
                map(() => true),
                catchError((error) => {
                    console.error(error);
                    throw error;
                }),
            );
    }

    retrieveSecurityQuestion(
        username: string,
        languageId: number,
    ): Observable<SecurityQuestion> {
        const params = new HttpParams()
            .set('username', username)
            .set('languageId', languageId.toString());

        return this._http
            .get(
                `${this._appConfig.OAUTH.AUTHORIZATION_ENDPOINT}/account/PasswordRecoveryGetQuestion`,
                { params },
            )
            .pipe(
                mapRetrieve(),
                map((model: ExtSecurityQuestion) =>
                    new SecurityQuestionModelTransformer().transform(model),
                ),
                catchError((error) => {
                    console.error(error);
                    throw error;
                }),
            );
    }

    requestResetPasswordTokenByQuestion(
        model: ResetPasswordSecQuesAns,
    ): Observable<string> {
        const body =
            new ResetPasswordSecQuesAnsModelTransformer().reverseTransform(
                model,
            );

        return this._http
            .post(
                `${this._appConfig.OAUTH.AUTHORIZATION_ENDPOINT}/account/PasswordRecoveryChallengResponseGetToken`,
                body,
            )
            .pipe(
                mapUpdate(),
                map((model: ExtResetPasswordToken) => model.tokenHash),
                catchError((error) => {
                    console.error(error);
                    throw error;
                }),
            );
    }

    verifyResetPasswordToken(
        tokenHash: string,
        languageId: number,
    ): Observable<number> {
        const params = new HttpParams()
            .set('tokenHash', tokenHash)
            .set('languageId', languageId.toString());

        return this._http
            .get(
                `${this._appConfig.OAUTH.AUTHORIZATION_ENDPOINT}/account/VerifyResetPasswordToken`,
                { params },
            )
            .pipe(
                mapUpdate(),
                map((model: ExtVerifyResetPasswordToken) => model.languageId),
                catchError((error) => {
                    console.error(error);
                    throw error;
                }),
            );
    }

    resetPassword(model: ResetPassword): Observable<boolean> {
        const body = new ResetPasswordModelTransformer().reverseTransform(
            model,
        );

        return this._http
            .post(
                `${this._appConfig.OAUTH.AUTHORIZATION_ENDPOINT}/account/PasswordRecoveryResetPassword`,
                body,
            )
            .pipe(
                mapUpdate(),
                map(() => true),
                catchError((error) => {
                    console.error(error);
                    throw error;
                }),
            );
    }

    requestResetPasswordTokenByUserId(userId: number): Observable<boolean> {
        const params = new HttpParams().set('userId', userId.toString());

        return this._http
            .get(
                `${this._appConfig.OAUTH.AUTHORIZATION_ENDPOINT}/account/PasswordResetByUserID`,
                { params },
            )
            .pipe(
                mapUpdate(),
                map(() => true),
                catchError((error) => {
                    console.error(error);
                    throw error;
                }),
            );
    }

    updatePassword(model: Password): Observable<boolean> {
        const body = new PasswordModelTransformer().reverseTransform(model);

        return this._http
            .post(
                `${this._appConfig.OAUTH.AUTHORIZATION_ENDPOINT}/account/ChangePassword`,
                body,
            )
            .pipe(
                mapUpdate(),
                map(() => true),
                catchError((error) => {
                    console.error(error);
                    throw error;
                }),
            );
    }

    permissions(): Observable<EffectiveUserPermission[]> {
        return this._http
            .get(
                `${this._appConfig.OAUTH.AUTHORIZATION_ENDPOINT}/authorization/permissions`,
            )
            .pipe(
                mapRetrieve(),
                map((model: ExtEffectiveUserPermission[]) =>
                    new EffectiveUserPermissionModelSetTransformer().transform(
                        model,
                    ),
                ),
                catchError(() => of([])),
            );
    }
}
